import { useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import { DicomTagMetadata, DicomTagRecord, TagDeidStrategy } from '@common/config/api/client'
import { CreateDicomTagRecord, DicomTagsDeidFormValues } from './DICOMTagsDeid'
import { useDicomTagsMetadata, useStudiesMetadataOptions } from '@study_setup/hooks/useStudyMetadataOptions'
import { Dropdown } from '@common/components/Form/Dropdown'
import VirtualizedDropdown from '@common/components/VirtualizedDropdown'
import { zodResolver } from '@hookform/resolvers/zod'
import { createStudyTagSchema } from '@study_setup/schemas/tagDeidSchema'
import BaseDialog from '@common/components/BaseDialog'
import { useCreateDicomTag } from '@features/tag_deid_management/hooks/useTagDeidQueries'

export interface CreateStudyTagDeidDialogProps {
  onClose: () => void
  isTagDeidGlobalView?: boolean
}

const CreateStudyTagDeidDialog = ({ onClose, isTagDeidGlobalView }: CreateStudyTagDeidDialogProps) => {
  const { valueRepresentations, isLoading: isValueRepresentationsLoading } = useStudiesMetadataOptions()
  const { data: dicomTags, isLoading: isDicomTagsLoading } = useDicomTagsMetadata()
  const { mutate: createDicomTag } = useCreateDicomTag()
  const { fields, insert } = useFieldArray<DicomTagsDeidFormValues, 'tags', 'id'>({
    name: 'tags',
  })

  type CreateStudyTagFormValues = {
    id: string
    tag_id: string
    tag_name: string
    value_representation_id: string
  }

  const form = useForm<CreateStudyTagFormValues>({
    resolver: zodResolver(createStudyTagSchema),
    mode: 'onBlur',
    defaultValues: {
      tag_id: '',
      tag_name: '',
      value_representation_id: '',
    },
  })

  const { control, reset, getValues, setValue } = form

  const dicomTagsIds = useMemo(
    () =>
      dicomTags?.map(({ tag_id }: DicomTagMetadata) => ({
        value: tag_id,
        label: tag_id,
      })) || [],
    [dicomTags],
  )

  const dicomTagsNames = useMemo(
    () =>
      dicomTags?.map(({ tag_name }: DicomTagMetadata) => ({
        value: tag_name,
        label: tag_name,
      })) || [],
    [dicomTags],
  )

  const handleTagIdDropdownChange = (_: React.SyntheticEvent, newTagId: string) => {
    setValue('tag_id', newTagId)
    const matchedTag = dicomTags?.find((tag) => tag.tag_id === newTagId)
    if (matchedTag) {
      setValue('tag_name', matchedTag.tag_name, { shouldValidate: true })
      setValue('value_representation_id', matchedTag.value_representation?.id ?? '', {
        shouldValidate: true,
      })
    }
  }

  const handleTagNameDropdownChange = (_: React.SyntheticEvent, newTagName: string) => {
    setValue('tag_name', newTagName)
    const matchedTag = dicomTags?.find((tag) => tag.tag_name === newTagName)
    if (matchedTag) {
      setValue('tag_id', matchedTag.tag_id, { shouldValidate: true })
      setValue('value_representation_id', matchedTag.value_representation?.id ?? '', {
        shouldValidate: true,
      })
    }
  }

  const validateTagConsistency = (values: CreateStudyTagFormValues): { isValid: boolean; message?: string } => {
    const { tag_id, tag_name, value_representation_id } = values

    const isEmptyTag = !tag_id && !tag_name && !value_representation_id
    if (isEmptyTag) return { isValid: true }

    const matchingTag = dicomTags?.find((tag) => tag.tag_id === tag_id || tag.tag_name === tag_name)
    if (!matchingTag) return { isValid: true }

    const isTagIdMatch = matchingTag.tag_id === tag_id
    const isTagNameMatch = matchingTag.tag_name === tag_name
    const isValueRepMatch = matchingTag.value_representation?.id === value_representation_id

    if (!isTagIdMatch || !isTagNameMatch || !isValueRepMatch) {
      return {
        isValid: false,
        message:
          "The provided tag information doesn't match the DICOM metadata. Please ensure all fields correspond to the same base tag or create a custom tag.",
      }
    }

    return { isValid: true }
  }

  const handleSave = () => {
    const values = getValues()
    const tagExists = fields.find(
      (tag: DicomTagRecord | CreateDicomTagRecord) => tag.tag_id === values.tag_id || tag.tag_name === values.tag_name,
    )

    if (tagExists) {
      toast(<Toast message="Tag already exists!" variant="error" />)
      return
    }

    const { isValid, message } = validateTagConsistency(values)
    if (!isValid) {
      toast(<Toast message={message!} variant="error" />)
      return
    }

    if (isTagDeidGlobalView) {
      const selectedTag = dicomTags?.find((tag) => tag.tag_id === values.tag_id)
      const dicomData = {
        dicom_tag_metadata_id: selectedTag?.id,
        tag_deid_strategy: 'UNCHANGE_TAG' as TagDeidStrategy,
        value: '',
      }

      createDicomTag(
        { dicomData },
        {
          onSuccess: () => {
            reset()
            onClose()
            toast(<Toast message="DICOM tag created successfully!" variant="success" />)
          },
          onError: (error) => {
            toast(<Toast message={error?.detail?.[0].msg || 'Failed to create tag.'} variant="error" />)
          },
        },
      )
    } else {
      insert(0, {
        tag_id: values.tag_id,
        tag_name: values.tag_name,
        tag_deid_strategy: 'UNCHANGE_TAG',
        value: '',
        value_representation_id: values.value_representation_id,
      })

      reset()
      onClose()
      toast(<Toast message="Study Tag created successfully!" variant="success" />)
    }
  }

  return (
    <BaseDialog
      title="Add DICOM Tag"
      description="Provide the DICOM tag ID and name"
      open={true}
      onClose={onClose}
      onSubmit={handleSave}
      form={form}
      submitLabel="Create Tag"
      data-testid="create-dicom-tag-dialog"
    >
      <VirtualizedDropdown
        name="tag_id"
        control={control}
        options={dicomTagsIds}
        loading={isDicomTagsLoading}
        label="Tag ID"
        freeSolo
        onInputChange={handleTagIdDropdownChange}
        aria-label="Select or enter DICOM Tag ID"
      />
      <VirtualizedDropdown
        name="tag_name"
        control={control}
        options={dicomTagsNames}
        loading={isDicomTagsLoading}
        label="Tag Name"
        freeSolo
        onInputChange={handleTagNameDropdownChange}
        aria-label="Select or enter DICOM Tag Name"
      />
      <Dropdown
        name="value_representation_id"
        control={control}
        options={valueRepresentations}
        loading={isValueRepresentationsLoading}
        label="Value Representation"
        helperText="Select the appropriate value representation for this DICOM tag"
      />
    </BaseDialog>
  )
}

export default CreateStudyTagDeidDialog
